import React, { useEffect, useState } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { toast } from 'react-toastify'

function formatDateTime(value) {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('it-IT')
  } catch {
    return String(value)
  }
}

function truncate(text, maxLength = 160) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}…`
}

function AiRequestsLogAdmin() {
  const { user } = useAuth()
  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  useEffect(() => {
    if (!isAdmin) return
    const now = new Date()
    const today = now.toISOString().slice(0, 10)
    const past = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
    const defaultFrom = past.toISOString().slice(0, 10)
    setFrom(defaultFrom)
    setTo(today)
  }, [isAdmin])

  const loadLogs = async (reset = false) => {
    if (!from || !to) return
    try {
      setLoading(true)
      const currentOffset = reset ? 0 : offset
      const res = await api.get('/ai/logs', {
        params: {
          from,
          to,
          limit,
          offset: currentOffset
        }
      })
      const data = res.data || {}
      const newRows = Array.isArray(data.rows) ? data.rows : []
      if (reset) {
        setRows(newRows)
      } else {
        setRows((prev) => [...prev, ...newRows])
      }
      setOffset(currentOffset + newRows.length)
      setHasMore(newRows.length === limit)
    } catch (e) {
      const backendMessage =
        e?.response?.data?.message ||
        (e?.response?.status ? `Errore ${e.response.status} nel caricamento del log AI` : null)
      toast.error(backendMessage || 'Errore nel caricamento del log AI')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    if (!from || !to) return
    setOffset(0)
    loadLogs(true)
  }, [isAdmin, from, to])

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">AI – Log richieste</h1>
        <p className="text-sm text-muted-foreground">Accesso riservato agli amministratori.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AI – Log richieste</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Elenco delle singole richieste AI con dettagli su utente, modello, token e prompt.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Dal giorno</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Al giorno</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => loadLogs(true)} disabled={loading || !from || !to}>
              {loading ? 'Caricamento...' : 'Aggiorna'}
            </Button>
            <Badge variant="outline" className="text-[10px]">
              Totale righe: {rows.length}
            </Badge>
          </div>
        </div>
      </Card>

      <Card className="p-4 overflow-x-auto">
        {loading && rows.length === 0 && (
          <div className="text-sm text-muted-foreground">Caricamento log richieste AI...</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-muted-foreground">
            Nessuna richiesta AI registrata nel periodo selezionato.
          </div>
        )}
        {rows.length > 0 && (
          <>
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Data/Ora</th>
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Utente</th>
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Email</th>
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Modello</th>
                  <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Prompt</th>
                  <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Completion</th>
                  <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Totale</th>
                  <th className="text-left py-2 pr-0 font-medium text-muted-foreground">Prompt elaborato</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {formatDateTime(r.createdAt)}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {r.userName || '-'}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {r.email || '-'}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {r.model || '-'}
                    </td>
                    <td className="py-1.5 pr-4 text-right whitespace-nowrap">
                      {r.promptTokens != null ? r.promptTokens.toLocaleString('it-IT') : 'n/d'}
                    </td>
                    <td className="py-1.5 pr-4 text-right whitespace-nowrap">
                      {r.completionTokens != null ? r.completionTokens.toLocaleString('it-IT') : 'n/d'}
                    </td>
                    <td className="py-1.5 pr-4 text-right whitespace-nowrap">
                      {r.totalTokens != null ? r.totalTokens.toLocaleString('it-IT') : 'n/d'}
                    </td>
                    <td className="py-1.5 pr-0 max-w-[420px]">
                      <span title={r.prompt || ''}>
                        {truncate(r.prompt || '', 160)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="mt-3 flex justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadLogs(false)}
                  disabled={loading}
                >
                  {loading ? 'Caricamento...' : 'Carica altri'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

export default AiRequestsLogAdmin

